import { decodeFloat32 } from "./schema";

export const LIGHTING_MAGIC = "VOXCITY_LIGHTING" as const;
export const LIGHTING_SCHEMA_VERSION = 1 as const;
export const STREET_LIGHT_STRIDE = 4 as const;
export const TRAFFIC_SIGNAL_STRIDE = 5 as const;

export interface CityLightingTileIndexEntry {
  id: string;
  x: number;
  z: number;
  url: string;
  byteLength: number;
  streetLightCount: number;
  trafficSignalCount: number;
}

export interface CityLightingManifest {
  magic: typeof LIGHTING_MAGIC;
  schemaVersion: typeof LIGHTING_SCHEMA_VERSION;
  tileSizeM: number;
  source: { id: string; sha256: string };
  showcases: {
    streetLight: { x: number; z: number };
    trafficSignal: { x: number; z: number };
  };
  tiles: CityLightingTileIndexEntry[];
}

export interface CityLightingTilePayload {
  magic: typeof LIGHTING_MAGIC;
  schemaVersion: typeof LIGHTING_SCHEMA_VERSION;
  id: string;
  streetLights: Uint8Array;
  trafficSignals: Uint8Array;
}

export interface DecodedCityLightingTile {
  id: string;
  streetLights: Float32Array;
  trafficSignals: Float32Array;
}

function finite(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function validateShowcase(value: unknown, label: string): void {
  if (!value || typeof value !== "object") throw new Error(`Lighting ${label} showcase is invalid`);
  const point = value as { x?: unknown; z?: unknown };
  if (!finite(point.x) || !finite(point.z))
    throw new Error(`Lighting ${label} showcase is invalid`);
}

export function validateCityLightingManifest(
  value: unknown,
): asserts value is CityLightingManifest {
  if (!value || typeof value !== "object") throw new Error("Lighting manifest is not an object");
  const manifest = value as Partial<CityLightingManifest>;
  if (manifest.magic !== LIGHTING_MAGIC) throw new Error("Invalid lighting manifest magic");
  if (manifest.schemaVersion !== LIGHTING_SCHEMA_VERSION)
    throw new Error("Unsupported lighting schema version");
  if (!finite(manifest.tileSizeM) || manifest.tileSizeM <= 0)
    throw new Error("Lighting manifest tile size is invalid");
  if (
    !manifest.source ||
    typeof manifest.source.id !== "string" ||
    manifest.source.id.length === 0 ||
    typeof manifest.source.sha256 !== "string" ||
    !/^[a-f0-9]{64}$/.test(manifest.source.sha256)
  )
    throw new Error("Lighting manifest source is invalid");
  if (!manifest.showcases) throw new Error("Lighting manifest showcases are missing");
  validateShowcase(manifest.showcases.streetLight, "street-light");
  validateShowcase(manifest.showcases.trafficSignal, "traffic-signal");
  if (!Array.isArray(manifest.tiles)) throw new Error("Lighting manifest tiles are invalid");
  const ids = new Set<string>();
  for (const tile of manifest.tiles) {
    if (
      !tile ||
      typeof tile.id !== "string" ||
      tile.id.length === 0 ||
      ids.has(tile.id) ||
      !Number.isInteger(tile.x) ||
      !Number.isInteger(tile.z) ||
      typeof tile.url !== "string" ||
      tile.url.length === 0 ||
      !Number.isInteger(tile.byteLength) ||
      tile.byteLength <= 0 ||
      !Number.isInteger(tile.streetLightCount) ||
      tile.streetLightCount < 0 ||
      !Number.isInteger(tile.trafficSignalCount) ||
      tile.trafficSignalCount < 0 ||
      tile.streetLightCount + tile.trafficSignalCount === 0
    )
      throw new Error(`Invalid lighting tile ${tile?.id ?? "unknown"}`);
    ids.add(tile.id);
  }
}

export function decodeCityLightingTile(
  value: unknown,
  expectedId: string,
): DecodedCityLightingTile {
  if (!value || typeof value !== "object")
    throw new Error(`Lighting tile ${expectedId} is not an object`);
  const tile = value as Partial<CityLightingTilePayload>;
  if (
    tile.magic !== LIGHTING_MAGIC ||
    tile.schemaVersion !== LIGHTING_SCHEMA_VERSION ||
    tile.id !== expectedId
  )
    throw new Error(`Lighting tile ${expectedId} schema is invalid`);
  if (!(tile.streetLights instanceof Uint8Array) || !(tile.trafficSignals instanceof Uint8Array))
    throw new Error(`Lighting tile ${expectedId} payload is invalid`);
  const streetLights = decodeFloat32(tile.streetLights);
  const trafficSignals = decodeFloat32(tile.trafficSignals);
  if (streetLights.length % STREET_LIGHT_STRIDE !== 0)
    throw new Error(`Lighting tile ${expectedId} street-light stride is invalid`);
  if (trafficSignals.length % TRAFFIC_SIGNAL_STRIDE !== 0)
    throw new Error(`Lighting tile ${expectedId} traffic-signal stride is invalid`);
  for (let offset = 0; offset < streetLights.length; offset += STREET_LIGHT_STRIDE) {
    const x = streetLights[offset];
    const z = streetLights[offset + 1];
    const yaw = streetLights[offset + 2];
    const height = streetLights[offset + 3];
    if (![x, z, yaw, height].every(finite) || height! < 4 || height! > 12)
      throw new Error(`Lighting tile ${expectedId} has an invalid street light`);
  }
  for (let offset = 0; offset < trafficSignals.length; offset += TRAFFIC_SIGNAL_STRIDE) {
    const x = trafficSignals[offset];
    const z = trafficSignals[offset + 1];
    const yaw = trafficSignals[offset + 2];
    const axis = trafficSignals[offset + 3];
    const crosswalk = trafficSignals[offset + 4];
    if (
      ![x, z, yaw, axis, crosswalk].every(finite) ||
      (axis !== 0 && axis !== 1) ||
      crosswalk! < 4 ||
      crosswalk! > 49
    )
      throw new Error(`Lighting tile ${expectedId} has an invalid traffic signal`);
  }
  return { id: expectedId, streetLights, trafficSignals };
}
